/**
 * Clipboard helpers for secrets (the member password reveal). They never log,
 * store or put the text in an error.
 */

/** The outcome of a clipboard write. */
export type ClipboardOutcome = "copied" | "failed";

type TextSettlement =
  | { ok: true; value: string }
  | { ok: false; error: unknown };

/**
 * Whether the asynchronous Clipboard API can be used: it exists only in a
 * secure context (https or localhost).
 */
export const isClipboardAvailable = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return Boolean(navigator.clipboard) && window.isSecureContext === true;
};

const clipboardItemConstructor = (): typeof ClipboardItem | undefined => {
  const candidate = (globalThis as { ClipboardItem?: typeof ClipboardItem })
    .ClipboardItem;
  return typeof candidate === "function" ? candidate : undefined;
};

/**
 * Writes the text the promise resolves to.
 *
 * Must be CALLED synchronously inside the click handler (before any `await`)
 * so the browser still sees the user activation: when `ClipboardItem` and
 * `navigator.clipboard.write` exist, a clipboard item whose content is the
 * still-pending text is handed to the browser at once. When that write is
 * refused and the text resolved, `writeText` is tried once.
 *
 * Rejects with the text promise's own error when it rejects (a reveal
 * refusal), so callers handle refusals in one place; any other failure
 * resolves to `'failed'`.
 */
export const writeSecretToClipboard = (
  text: Promise<string>
): Promise<ClipboardOutcome> => {
  // Observed once, so a rejected text never surfaces as an unhandled rejection.
  const settled: Promise<TextSettlement> = text.then(
    (value) => ({ ok: true, value }),
    (error: unknown) => ({ ok: false, error })
  );
  const clipboard =
    typeof navigator === "undefined" ? undefined : navigator.clipboard;
  const Item = clipboardItemConstructor();

  let itemWrite: Promise<void> | null = null;
  if (clipboard && typeof clipboard.write === "function" && Item) {
    try {
      const blob = text.then(
        (value) => new Blob([value], { type: "text/plain" })
      );
      // The browser consumes the rejection; this only keeps it handled.
      blob.catch(() => undefined);
      itemWrite = clipboard.write([new Item({ "text/plain": blob })]);
    } catch {
      itemWrite = null;
    }
  }

  return (async (): Promise<ClipboardOutcome> => {
    let written = false;
    if (itemWrite) {
      try {
        await itemWrite;
        written = true;
      } catch {
        written = false;
      }
    }
    const result = await settled;
    if (result.ok === false) {
      throw result.error;
    }
    if (written) {
      return "copied";
    }
    if (clipboard && typeof clipboard.writeText === "function") {
      try {
        await clipboard.writeText(result.value);
        return "copied";
      } catch {
        return "failed";
      }
    }
    return "failed";
  })();
};
